<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Config } from '@/Config'
import * as Server from '@/services/Server'
import * as Alerts from '@/services/Alerts'
import ConfirmDialog from '@/components/ConfirmDialog.vue'

/**
 * "Start over" on an event that admits one attempt per track (see racing_events_laptime.md): a stage
 * already left can never be improved, so erasing the results is the only way back. It erases the
 * player's own results — the standings are recomputed server side — and hands the caller the updated
 * event to put back in place.
 *
 * It decides by itself whether it has anything to offer, out of the event payload the caller already
 * has (the detail of the event page, or a card of the list): `playable`, `singleAttempt` and at
 * least one result on a track or one attempt that is not finished. The result is what
 * `myEntry.played` counts — the entry alone is not enough: a row can linger with no result at all
 * (data cleaned by hand, rows from before the result was stored), and there would be nothing to
 * start over. The attempts matter on a hardcore event: an abandoned stage occupies its track with
 * no result behind it, and starting over is the widest way to get it back. Written that way the
 * caller has no rule to duplicate — it just drops the component next to the play button.
 *
 * The component renders the dialog as well, so `class` and the like have to be bound by hand.
 *
 * The button is full width but **not** `block`: that prop is `flex: 1 0 auto`, and the call-to-action
 * card is a flex column, so `block` would grow it along the height. `w-100` gives the width and leaves
 * the height to the button.
 */
defineOptions({ inheritAttrs: false })

const props = defineProps({
  /** The event as the API serves it (EventLib::detail or EventLib::present). */
  event: { type: Object, required: true },
})

const emit = defineEmits(['reset'])

const { t } = useI18n()

const confirming = ref(false)
const resetting = ref(false)

const canReset = computed(() => props.event.playable === true
  && props.event.singleAttempt === true
  && ((props.event.myEntry?.played ?? 0) > 0 || (props.event.myAttempts?.length ?? 0) > 0))

async function startOver() {
  resetting.value = true

  try {
    const response = await Server.del(Config.server.routes.api.events_reset(props.event.id))
    emit('reset', response.data?.data ?? null)
    Alerts.success(t('events.resetDone'))
  } catch (error) {
    Alerts.error(error.response?.data?.message ?? t('events.resetError'))
  } finally {
    resetting.value = false
  }
}
</script>

<template>
  <v-btn
    v-if="canReset"
    v-bind="$attrs"
    variant="tonal"
    color="warning"
    class="w-100"
    :loading="resetting"
    @click="confirming = true"
  >
    {{ $t('events.resetResults') }}
  </v-btn>

  <!-- Outside the condition above: the dialog has to stay mounted while it closes, and the button is
       gone the moment the caller takes the emptied event. -->
  <ConfirmDialog
    v-model="confirming"
    :title="$t('events.resetTitle')"
    :text="$t('events.resetText')"
    :confirm-text="$t('events.resetConfirm')"
    :cancel-text="$t('common.cancel')"
    confirm-color="warning"
    @confirm="startOver"
  />
</template>
